import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { Calculator, Check, HelpCircle, Ruler, PenTool } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '@/utils/api';

const Pricing = () => {
  const [activeTab, setActiveTab] = useState('measure');
  const [roomCount, setRoomCount] = useState(3);
  const [addColour, setAddColour] = useState(false);
  const [add3d, setAdd3d] = useState(false);
  const [addFurniture, setAddFurniture] = useState(false);
  const [addSymbols, setAddSymbols] = useState(false);
  const [elevationsCount, setElevationsCount] = useState(0);
  const [priceResult, setPriceResult] = useState(null);

  useEffect(() => {
    // Recompute the indicative price whenever any pricing input changes.
    // Defined inline so ESLint sees the full dependency chain and won't warn.
    const calc = async () => {
      try {
        const response = await axios.post(`${API_URL}/calculate-price`, {
          room_count: parseInt(roomCount, 10) || 1,
          add_colour: addColour,
          add_3d: add3d,
          add_furniture: addFurniture,
          add_symbols: addSymbols,
          elevations_count: parseInt(elevationsCount, 10) || 0,
          guide_only: false,
        });
        setPriceResult(response.data);
      } catch (error) {
        console.error('Error calculating price:', error);
      }
    };
    calc();
  }, [roomCount, addColour, add3d, addFurniture, addSymbols, elevationsCount]);

  const calculatePrice = async () => {
    try {
      const response = await axios.post(`${API_URL}/calculate-price`, {
        room_count: parseInt(roomCount, 10) || 1,
        add_colour: addColour,
        add_3d: add3d,
        add_furniture: addFurniture,
        add_symbols: addSymbols,
        elevations_count: parseInt(elevationsCount, 10) || 0,
        guide_only: false
      });
      setPriceResult(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Pricing", path: "/pricing" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        pageId="pricing"
        defaultTitle="Floor Plan Pricing | House Plan Direct"
        defaultDescription="Transparent fixed pricing for sketch to CAD conversion. Old plans redrawn from £40. No hidden fees. Instant price calculator for CAD floor plans, DWG conversion, and planning drawings."
        aiSummary="Simple, transparent pricing for hand sketch to CAD conversion and old plan redrawing. Base prices: up to 3 rooms £40, additional rooms £5 each. All plans include PDF, JPEG, and DWG formats. Optional extras: colour floor plans (+£10), 3D visualisation (+£20), furniture layout (+£25), symbols and key (+£10)."
        keywords="sketch to CAD pricing, old plans redrawn cost, CAD floor plan pricing UK, DWG conversion cost, planning drawings price"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-8" data-testid="pricing-header">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Pricing</h1>
          <p className="text-lg text-gray-600">
            Two clearly-separated services. Pick the one you need.
          </p>
        </div>

        {/* Service Tabs */}
        <div className="mx-auto mb-10 flex max-w-2xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="pricing-service-tabs">
          <button
            type="button"
            onClick={() => setActiveTab('measure')}
            data-testid="pricing-tab-measure"
            className={`flex flex-1 items-center justify-center gap-2 px-4 py-4 text-sm font-semibold transition-colors sm:text-base ${
              activeTab === 'measure'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Ruler className="h-5 w-5" />
            Create Your Own — Free
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('managed')}
            data-testid="pricing-tab-managed"
            className={`flex flex-1 items-center justify-center gap-2 px-4 py-4 text-sm font-semibold transition-colors sm:text-base ${
              activeTab === 'managed'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <PenTool className="h-5 w-5" />
            Managed Redraw Service
          </button>
        </div>

        {activeTab === 'measure' && (
          <div className="mx-auto max-w-3xl" data-testid="pricing-measure-panel">
            <div className="rounded-xl border border-emerald-200 bg-white p-8 shadow-lg">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600 text-white">
                  <Ruler className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Create Your Own Floor Plan</h2>
                  <p className="text-sm text-gray-500">Self-service, in your browser — Plan Measure, Floor Plan Creator &amp; Layout</p>
                </div>
              </div>

              <div className="mb-6 flex items-baseline gap-2" data-testid="measure-price-headline">
                <span className="text-5xl font-bold text-emerald-600">Free</span>
                <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">to create &amp; edit</span>
              </div>
              <p className="mb-6 text-sm text-gray-600">£2.99 one-off to unlock the finished, watermark-free project — unlimited amendments and re-exports included.</p>

              <ul className="mb-6 space-y-3 text-gray-700">
                <li className="flex items-start">
                  <Check className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  Draw a floor plan from scratch, or upload a PDF/image and measure it
                </li>
                <li className="flex items-start">
                  <Check className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  Calibrate to real-world scale, then measure lengths, areas, angles
                </li>
                <li className="flex items-start">
                  <Check className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  Present a professional Marketing Plan layout and export PNG / PDF / CSV / JSON
                </li>
                <li className="flex items-start">
                  <Check className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  Projects saved locally to your browser — nothing uploaded to our servers
                </li>
                <li className="flex items-start">
                  <Check className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  Illustrative &amp; preliminary measurement only — not a survey
                </li>
              </ul>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Link to="/measurement-tool" className="flex-1">
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    size="lg"
                    data-testid="buy-plan-measure-btn"
                  >
                    Start creating free
                  </Button>
                </Link>
              </div>

              <p className="mt-6 text-xs text-gray-500">
                Prefer us to do it for you?{' '}
                <button
                  type="button"
                  onClick={() => setActiveTab('managed')}
                  className="text-blue-600 underline hover:text-blue-700"
                  data-testid="pricing-switch-to-managed"
                >
                  We draw your plan from £40 →
                </button>
              </p>
            </div>
          </div>
        )}

        {activeTab === 'managed' && (
        <div className="grid lg:grid-cols-2 gap-8" data-testid="pricing-managed-panel">
          {/* Pricing Calculator */}
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200" data-testid="price-calculator">
            <div className="flex items-center space-x-3 mb-6">
              <Calculator className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Price Calculator</h2>
            </div>

            <div className="space-y-6">
              {/* Room Count */}
              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="roomCount" className="text-sm font-medium">Total Number of Rooms</Label>
                  <span className="relative inline-flex group" data-testid="pricing-room-count-help">
                    <HelpCircle className="h-4 w-4 text-blue-600 cursor-help" />
                    <span className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 z-20 w-72 rounded-lg bg-gray-900 text-white text-xs leading-relaxed p-3 shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                      <strong className="block mb-1 text-blue-300">Worked example</strong>
                      A typical 3-bed semi has <strong>~8 rooms</strong>: 3 bedrooms + kitchen + lounge + dining + 2 bathrooms.
                      <br />A 2-bed flat usually has <strong>~5 rooms</strong>: 2 bedrooms + kitchen + lounge + bathroom.
                    </span>
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 mb-1">
                  Include <strong>every room</strong> — bedrooms, kitchen, lounge, bathrooms, hallway, etc.
                  Exclude cupboards & corridors.
                </p>
                <Input
                  id="roomCount"
                  type="number"
                  min="1"
                  value={roomCount}
                  onChange={(e) => setRoomCount(e.target.value)}
                  className="mt-1"
                  data-testid="room-count-input"
                />
              </div>

              {/* Extras */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Optional Extras</Label>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="colour"
                    checked={addColour}
                    onCheckedChange={setAddColour}
                    data-testid="colour-checkbox"
                  />
                  <label htmlFor="colour" className="text-sm cursor-pointer flex-1">
                    Add colour (+£10)
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="3d"
                    checked={add3d}
                    onCheckedChange={setAdd3d}
                    data-testid="3d-checkbox"
                  />
                  <label htmlFor="3d" className="text-sm cursor-pointer flex-1">
                    Add 3D visualisation (Price on request)
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="furniture"
                    checked={addFurniture}
                    onCheckedChange={setAddFurniture}
                    data-testid="furniture-checkbox"
                  />
                  <label htmlFor="furniture" className="text-sm cursor-pointer flex-1">
                    Add furniture (+£25)
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="symbols"
                    checked={addSymbols}
                    onCheckedChange={setAddSymbols}
                    data-testid="symbols-checkbox"
                  />
                  <label htmlFor="symbols" className="text-sm cursor-pointer flex-1">
                    Add symbols & key (+£25)
                  </label>
                </div>

                {/* Elevations & Sections Add-on */}
                <div className="pt-3 border-t border-gray-200">
                  <Label htmlFor="elevations" className="text-sm font-medium">Add Elevations & Sections (+£65 each)</Label>
                  <p className="text-xs text-gray-500 mb-2">Save £20 per elevation when ordering with a floor plan</p>
                  <select
                    id="elevations"
                    value={elevationsCount}
                    onChange={(e) => setElevationsCount(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="elevations-select"
                  >
                    <option value="0">None</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num} - £{num * 65}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            {priceResult && (
              <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200" data-testid="price-breakdown">
                <h3 className="font-semibold text-gray-900 mb-4">Price Breakdown</h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(priceResult.breakdown).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600">{value}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-3 border-t border-blue-300 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-blue-600" data-testid="total-price">£{priceResult.total_price.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <Link 
              to={`/order?rooms=${roomCount}&colour=${addColour}&3d=${add3d}&furniture=${addFurniture}&symbols=${addSymbols}&elevations=${elevationsCount}`} 
              className="block mt-6"
            >
              <Button className="w-full bg-blue-600 hover:bg-blue-700" size="lg" data-testid="proceed-order-btn">
                Proceed to Order
              </Button>
            </Link>
          </div>

          {/* Pricing Information */}
          <div className="space-y-6">
            {/* Base Pricing */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200" data-testid="base-pricing">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Base Plan Pricing</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">£40 for properties with up to 3 rooms</span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">+£5 for each additional room above 3</span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Cupboards and corridors are excluded from room count</span>
                </div>
              </div>
            </div>

            {/* Extras Pricing */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200" data-testid="extras-pricing">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Optional Extras</h3>
              <div className="space-y-3 text-gray-600">
                <div className="flex justify-between">
                  <span>Add colour</span>
                  <span className="font-semibold">+£10</span>
                </div>
                <div className="flex justify-between">
                  <span>Add 3D visualisation</span>
                  <span className="font-semibold text-blue-600">Price on request</span>
                </div>
                <div className="flex justify-between">
                  <span>Add furniture</span>
                  <span className="font-semibold">+£25</span>
                </div>
                <div className="flex justify-between">
                  <span>Add symbols & key</span>
                  <span className="font-semibold">+£25</span>
                </div>
              </div>
            </div>

            {/* Elevations & Sections - Standalone */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200" data-testid="elevations-pricing">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Elevations & Sections</h3>
              <div className="space-y-3 mb-4">
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600"><strong>£85</strong> per elevation or section</span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Professional CAD elevation drawings</span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Order up to 10 elevations/sections</span>
                </div>
              </div>
              <Link to="/order?elevations_only=true">
                <Button className="w-full bg-purple-600 hover:bg-purple-700" data-testid="buy-elevations-only-btn">
                  Order Elevations Only
                </Button>
              </Link>
            </div>

            {/* Measuring Guide */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200" data-testid="guide-pricing">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Measuring Guidance PDF</h3>
              <div className="space-y-3 mb-4">
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600"><strong>FREE</strong> when purchasing a floor plan</span>
                </div>
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600"><strong>£2</strong> when purchased separately</span>
                </div>
              </div>
              <Link to="/order?guide_only=true">
                <Button className="w-full bg-green-600 hover:bg-green-700" data-testid="buy-guide-only-btn">
                  Purchase Guide Only - £2
                </Button>
              </Link>
            </div>

            {/* Additional Info */}
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200" data-testid="additional-info">
              <h4 className="font-semibold text-gray-900 mb-3">Important Information</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-yellow-600 font-bold mr-2">•</span>
                  <span>We will correct any errors in the conversion that are our responsibility at no extra charge.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-600 font-bold mr-2">•</span>
                  <span>Any amendments required by you are charged at £25 per hour (minimum charge £10).</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Pricing;
